// Shared session/auth helpers used by both the axios layer and React code.
// Centralizes token storage (rotation-aware), session teardown, the idle-timeout
// window, and the "why were you logged out" message handed to the login screen
// across a redirect.

export const IDLE_TIMEOUT_KEY = 'idle_timeout_seconds';
export const DEFAULT_IDLE_TIMEOUT = 3600; // seconds; backend currently sends this

// Custom event dispatched by axios on every successful authenticated response so
// the idle manager can treat real API traffic as a server-window slide.
export const ACTIVITY_EVENT = 'app:api-activity';

const LOGOUT_REASON_KEY = 'logout_reason';

export const LOGOUT_MESSAGES = {
  token_revoked:
    "You've been signed out — your account was signed in on another device or your password was changed.",
  session_idle: 'You were logged out due to inactivity.',
  default: 'Your session has expired. Please sign in again.',
};

// Read the persisted idle window (seconds), falling back to the default.
export const getIdleTimeoutSeconds = () => {
  const raw = localStorage.getItem(IDLE_TIMEOUT_KEY);
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_IDLE_TIMEOUT;
};

// Persist the idle window from a login/otp/refresh/keep-alive response.
export const setIdleTimeoutSeconds = (seconds) => {
  const parsed = Number(seconds);
  if (Number.isFinite(parsed) && parsed > 0) {
    localStorage.setItem(IDLE_TIMEOUT_KEY, String(parsed));
  }
};

// Store tokens from a login/otp/refresh response. Honors refresh-token rotation:
// uses data.refresh when the backend returns a new one, otherwise keeps the
// existing refresh token. Also persists idle_timeout_seconds when present.
export const storeTokens = (data) => {
  if (!data || !data.access) return;

  let existingRefresh = null;
  try {
    existingRefresh = JSON.parse(localStorage.getItem('authTokens'))?.refresh || null;
  } catch (err) {
    existingRefresh = null;
  }

  const newTokens = {
    access: data.access,
    refresh: data.refresh || existingRefresh,
  };
  localStorage.setItem('authTokens', JSON.stringify(newTokens));

  if (data.idle_timeout_seconds != null) {
    setIdleTimeoutSeconds(data.idle_timeout_seconds);
  }
};

// Tear down all client-side session state.
export const clearSession = () => {
  localStorage.removeItem('authTokens');
  localStorage.removeItem('cloudfront_cookie_expiry');
  localStorage.removeItem('persist:root');
  localStorage.removeItem('prevUrl');
  localStorage.removeItem(IDLE_TIMEOUT_KEY);
};

// Record why the user is being logged out so the login screen can explain it.
export const setLogoutReason = (code) => {
  try {
    sessionStorage.setItem(LOGOUT_REASON_KEY, code || 'default');
  } catch (err) {
    /* sessionStorage unavailable — non-fatal */
  }
};

// Read + clear the logout reason, returning a user-facing message (or null).
export const consumeLogoutMessage = () => {
  let code = null;
  try {
    code = sessionStorage.getItem(LOGOUT_REASON_KEY);
    if (code) sessionStorage.removeItem(LOGOUT_REASON_KEY);
  } catch (err) {
    return null;
  }
  if (!code) return null;
  return LOGOUT_MESSAGES[code] || LOGOUT_MESSAGES.default;
};
